/**
 * Step: How do your custom files arrive? (BLU-1399, Phase 95 PR 3).
 *
 * Shown in the client flow only when the client sells custom or both, and
 * always in the owner flow (CF-5), right after Sync products in both.
 *
 * Choices (OptionCard radios):
 *  - PixelWrangler: an admin pastes the PixelWrangler API key once
 *    (`onboarding.savePwKey`). The server checks it with PixelWrangler first
 *    and stores it only if PixelWrangler accepts it (CF-1). It decides where it
 *    goes (its own Bridge for an org that runs its own floor, every active 3PL
 *    for a pure client) and the key is never shown again. A save that stores
 *    the key finishes the step.
 *  - Links on the Shopify order: the SAME `CustomArtworkSourceCard` Settings,
 *    Integrations uses, embedded (front / back field names, no sizing), plus
 *    "Check recent orders" (`artworkSourceConfig.checkRecentOrders`, admin):
 *    found, https and fetchable per saved field, booleans and a count only. A
 *    Check where every saved field passes on at least one order finishes the
 *    step.
 *  - Our API: shown as Coming soon, not selectable.
 *  - We don't sell custom products (owner flow only, `allowNone`): finishes
 *    the step with the page's step-flag write and saves nothing else (CF-5).
 *
 * Where the key is saved (`onboarding.getCustomFiles`, read only while the step
 * is open, cached 60 s, no focus refetch): per 3PL by name, or the org's own
 * Bridge, or a note when there is nowhere to save it yet (CF-6). The step opens
 * on PixelWrangler once when a key is already saved, else on Links when a
 * field name is saved (`getConfig`, the card's own cache entry). An accepted Save patches
 * that cache from its own result (no refetch) unless the number of places
 * changed since the read; a refused or failed Save patches nothing (W1).
 *
 * Colour codes (CC-4, CC-5, N9): once a target's key is saved, the
 * PixelWrangler path also shows the Customizer colour list (the same
 * `CustomizerColourCodes`, one `getCustomizerColours` read shared with Product
 * sync) and a link to its lasting home, `/product-sync?tab=code#customizer`.
 * Never on Links, None or a no-target note.
 *
 * A locked pane renders its frame only: the body (and its query and mutation
 * hooks) mounts only while the step is open.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SecretInput } from '@/components/ui/secret-input';
import { useRole } from '@/hooks/use-role';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { CustomArtworkSourceCard } from '@/features/organization/components/CustomArtworkSourceCard';
import { useSavedHold } from '../../hooks/useSavedHold';
import { StepPane } from '../shell/StepPane';
import { useStepChrome } from '../shell/StepChrome';
import { OptionCard } from '../shell/OptionCard';
import { SavePrimaryButton } from '../shell/SavePrimaryButton';
import { PaneActionButton } from '../shell/PaneActionButton';
import { PwSavedList } from '../custom-files/PwSavedList';
import { CustomizerColourCodes } from '../custom-files/CustomizerColourCodes';
import { CUSTOMIZER_SETTINGS_HREF } from '@/features/product-sync/lib/tabs';

interface StepCustomFilesProps {
  /** The org's Shopify store domain is set (PixelWrangler files are matched to it). */
  shopConnected: boolean;
  /** Offer "We don't sell custom products" (the owner flow, CF-5). */
  allowNone: boolean;
  /** Mark the step done (the page skips the write when it already reads done). */
  onPassed: () => void;
}

type SavePwKeyResult = RouterOutputs['onboarding']['savePwKey'];
type CustomFilesView = RouterOutputs['onboarding']['getCustomFiles'];
type SaveFailure = Extract<SavePwKeyResult, { ok: false }>['reason'];

/** One full literal key per refusal, typed against the result union (a new reason fails the build). */
const FAILURE_KEY: Record<SaveFailure, string> = {
  org_inactive: 'stepCustomFiles.pw.orgInactive',
  no_3pl_yet: 'stepCustomFiles.pw.no3plYet',
  no_print_location: 'stepCustomFiles.pw.noPrintLocation',
  shop_not_connected: 'stepCustomFiles.pw.shopFirst',
  pw_check_failed: 'stepCustomFiles.pw.checkFailed',
  key_not_stored: 'stepCustomFiles.pw.keyNotStored',
};

type Choice = 'pw' | 'links' | 'none';

type CheckResult = RouterOutputs['artworkSourceConfig']['checkRecentOrders'];
type CheckFailure = Extract<CheckResult, { ok: false }>['reason'];
type CheckSide = Extract<CheckResult, { ok: true }>['front'];

/** One full literal key per Check refusal (a new reason fails the build). */
const CHECK_FAILURE_KEY: Record<CheckFailure, string> = {
  no_keys: 'stepCustomFiles.links.noKeys',
  shop_not_connected: 'stepCustomFiles.links.shopNotConnected',
  shopify_error: 'stepCustomFiles.links.checkError',
};

function sideResultKey(side: CheckSide): string {
  if (!side.found) return 'stepCustomFiles.links.result.notFound';
  if (!side.https) return 'stepCustomFiles.links.result.foundNotHttps';
  if (!side.allowed) return 'stepCustomFiles.links.result.foundNotAllowed';
  return 'stepCustomFiles.links.result.foundHttps';
}

/** The Check passes when orders were read and every saved field is a fetchable https link. */
function checkPassed(result: CheckResult): boolean {
  if (!result.ok || result.ordersChecked === 0) return false;
  const sides = [result.front, result.back].filter((side) => side.configured);
  return sides.length > 0 && sides.every((side) => side.found && side.https && side.allowed);
}

const MIN_KEY_LENGTH = 8;

/** The no-target note (CF-6), as full literal keys. */
const EMPTY_KEY: Record<NonNullable<CustomFilesView['emptyReason']>, string> = {
  no_3pl_yet: 'stepCustomFiles.pw.no3plYet',
  no_print_location: 'stepCustomFiles.pw.noPrintLocation',
};

export function StepCustomFiles({ shopConnected, allowNone, onPassed }: StepCustomFilesProps) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();
  const frame = { title: t('stepCustomFiles.title'), description: t('stepCustomFiles.description') };
  // A locked pane shows only its frame: nothing below mounts.
  if (state === 'locked') return <StepPane {...frame} />;
  return <StepCustomFilesBody frame={frame} shopConnected={shopConnected} allowNone={allowNone} onPassed={onPassed} />;
}

function StepCustomFilesBody({
  frame,
  shopConnected,
  allowNone,
  onPassed,
}: { frame: { title: string; description: string } } & StepCustomFilesProps) {
  const { t } = useTranslation('onboarding');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const groupName = useId();
  const choiceLabelId = useId();
  const keyId = useId();
  const keyHintId = useId();
  const adminHintId = useId();
  const hold = useSavedHold();
  const [choice, setChoice] = useState<Choice | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const utils = trpc.useUtils();
  const view = trpc.onboarding.getCustomFiles.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  // The embedded card's own cache entry, so one request serves both.
  const config = trpc.artworkSourceConfig.getConfig.useQuery(undefined, { staleTime: 60_000 });
  const save = trpc.onboarding.savePwKey.useMutation();
  const check = trpc.artworkSourceConfig.checkRecentOrders.useMutation();
  const [checkResult, setCheckResult] = useState<CheckResult | { ok: false; reason: 'error' } | null>(null);

  // Open once on the saved choice: PixelWrangler when a key is saved somewhere,
  // else Links when a field name is saved. Waits for both reads to settle; a
  // later refetch never overrides the operator's pick.
  const preselected = useRef(false);
  const viewSettled = view.data !== undefined || view.isError;
  const configSettled = config.data !== undefined || config.isError;
  useEffect(() => {
    if (preselected.current || !viewSettled || !configSettled) return;
    preselected.current = true;
    const pwSaved = view.data?.targets.some((target) => target.saved) === true;
    const linksSaved = !!(config.data?.frontAttrKey || config.data?.backAttrKey);
    const saved: Choice | null = pwSaved ? 'pw' : linksSaved ? 'links' : null;
    if (saved) setChoice((current) => current ?? saved);
  }, [viewSettled, configSettled, view.data, config.data]);

  const emptyReason = view.data?.emptyReason ?? null;
  const keySaved = view.data?.targets.some((target) => target.saved) === true;
  const canEnterKey = shopConnected && emptyReason === null;

  const trimmed = apiKey.trim();
  const saveDisabled = !admin || save.isPending || trimmed.length < MIN_KEY_LENGTH;

  const onSave = () => {
    if (saveDisabled) return;
    setMessage(null);
    save.mutate(
      { apiKey: trimmed },
      {
        onSuccess: (result) => {
          if (result.ok) {
            setApiKey('');
            hold.markSaved();
            setMessage({ tone: 'ok', text: t('stepCustomFiles.pw.saved') });
            void markViewSaved(result.targetCount);
            dropStaleNotConnected();
            onPassed();
            return;
          }
          setMessage({ tone: 'error', text: t(FAILURE_KEY[result.reason]) });
        },
        onError: () => setMessage({ tone: 'error', text: t('stepCustomFiles.pw.saveError') }),
      },
    );
  };

  /** Patch the where-saved cache from the save's own result; refetch only when the places changed (W1). */
  const markViewSaved = async (targetCount: number) => {
    await utils.onboarding.getCustomFiles.cancel();
    const cached = utils.onboarding.getCustomFiles.getData();
    if (!cached || cached.targets.length !== targetCount) {
      void utils.onboarding.getCustomFiles.invalidate();
      return;
    }
    utils.onboarding.getCustomFiles.setData(undefined, (prev) =>
      prev ? { ...prev, targets: prev.targets.map((target) => ({ ...target, saved: true })) } : prev,
    );
  };

  /**
   * A colour list cached as not connected (read before this save made the
   * PixelWrangler row) would show "Connect PixelWrangler" under a saved key for
   * up to 60 s. Mark only that entry stale; any other cached view stays.
   */
  const dropStaleNotConnected = () => {
    const colours = utils.onboarding.getCustomizerColours.getData();
    if (colours && !colours.connected) void utils.onboarding.getCustomizerColours.invalidate();
  };

  const hasSavedField = !!(config.data?.frontAttrKey || config.data?.backAttrKey);
  const checkDisabled = !admin || check.isPending || !hasSavedField;

  const onCheck = () => {
    if (checkDisabled) return;
    setCheckResult(null);
    check.mutate(undefined, {
      onSuccess: (result) => {
        setCheckResult(result);
        if (checkPassed(result)) onPassed();
      },
      onError: () => setCheckResult({ ok: false, reason: 'error' }),
    });
  };

  const onChoose = (value: string) => {
    if (value === 'pw' || value === 'links' || value === 'none') {
      setChoice(value);
      setMessage(null);
    }
  };

  let primary: ReactNode = undefined;
  if (choice === 'none') {
    primary = (
      <PaneActionButton type="button" onClick={onPassed}>
        {t('stepCustomFiles.none.confirm')}
      </PaneActionButton>
    );
  } else if (choice === 'links') {
    primary = (
      <PaneActionButton
        type="button"
        onClick={onCheck}
        disabled={checkDisabled}
        aria-describedby={!admin ? adminHintId : undefined}
      >
        {t('stepCustomFiles.links.check')}
      </PaneActionButton>
    );
  } else if (choice === 'pw' && canEnterKey) {
    primary = (
      <SavePrimaryButton
        phase={hold.phase(save.isPending)}
        disabled={saveDisabled}
        onClick={onSave}
        describedBy={!admin ? adminHintId : undefined}
      >
        <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
        {t('stepCustomFiles.pw.save')}
      </SavePrimaryButton>
    );
  }

  return (
    <StepPane
      {...frame}
      primary={primary}
      footerHint={
        ((choice === 'pw' && canEnterKey) || choice === 'links') && !admin ? (
          <span id={adminHintId}>{t('stepCustomFiles.adminOnly')}</span>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <div className="space-y-2.5">
          <p id={choiceLabelId} className="text-sm font-medium text-foreground">
            {t('stepCustomFiles.choiceLabel')}
          </p>
          <div
            role="radiogroup"
            aria-labelledby={choiceLabelId}
            className="grid gap-2.5 sm:grid-cols-2"
          >
            <OptionCard
              name={groupName}
              value="pw"
              checked={choice === 'pw'}
              onChange={onChoose}
              label={t('stepCustomFiles.pw.label')}
              body={t('stepCustomFiles.pw.body')}
            />
            <OptionCard
              name={groupName}
              value="links"
              checked={choice === 'links'}
              onChange={onChoose}
              label={t('stepCustomFiles.links.label')}
              body={t('stepCustomFiles.links.body')}
            />
            <OptionCard
              name={groupName}
              value="api"
              checked={false}
              onChange={onChoose}
              label={t('stepCustomFiles.api.label')}
              body={t('stepCustomFiles.api.body')}
              disabled
              disabledReason={t('stepCustomFiles.api.soon')}
            />
            {allowNone && (
              <OptionCard
                name={groupName}
                value="none"
                checked={choice === 'none'}
                onChange={onChoose}
                label={t('stepCustomFiles.none.label')}
                body={t('stepCustomFiles.none.body')}
              />
            )}
          </div>
          {view.isLoading && <Skeleton className="h-4 w-56" />}
          {view.isError && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
              <p className="text-xs text-destructive">{t('stepCustomFiles.loadError')}</p>
              <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={() => void view.refetch()}>
                {t('states.retry')}
              </Button>
            </div>
          )}
        </div>

        {choice === 'pw' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{t('stepCustomFiles.pw.fieldsNote')}</p>
            {emptyReason !== null ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {t(EMPTY_KEY[emptyReason])}
              </p>
            ) : !shopConnected ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {t('stepCustomFiles.pw.shopFirst')}
              </p>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor={keyId}>{t('stepCustomFiles.pw.keyLabel')}</Label>
                <SecretInput
                  id={keyId}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  disabled={!admin || save.isPending}
                  wrapperClassName="max-w-prose"
                  className="font-mono"
                  aria-describedby={keyHintId}
                />
                <p id={keyHintId} className="text-xs text-muted-foreground">
                  {t('stepCustomFiles.pw.keyHint')}
                </p>
                <p className="text-xs text-muted-foreground">{t('stepCustomFiles.pw.checkNote')}</p>
              </div>
            )}
            {view.data && <PwSavedList view={view.data} />}
            {/* CC-4, N9: the colour list only once a target's key is saved (the patched cache counts). */}
            {emptyReason === null && keySaved ? (
              <div className="space-y-3 border-t pt-3">
                <CustomizerColourCodes showSetupLink={false} />
                <p className="text-xs text-muted-foreground">
                  {t('stepCustomFiles.pw.coloursLater')}{' '}
                  <Link to={CUSTOMIZER_SETTINGS_HREF} className="font-medium text-threadheads-purple hover:underline">
                    {t('stepCustomFiles.pw.coloursLaterLink')}
                  </Link>
                </p>
              </div>
            ) : null}
          </div>
        )}

        {choice === 'links' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">{t('stepCustomFiles.links.keysNote')}</p>
            <CustomArtworkSourceCard embedded onSaved={() => setCheckResult(null)} />
            <p className="text-xs text-muted-foreground">{t('stepCustomFiles.links.checkNote')}</p>
            <div aria-live="polite" className="space-y-1">
              {checkResult && !checkResult.ok && (
                <p className="text-xs text-destructive">
                  {checkResult.reason === 'error'
                    ? t('stepCustomFiles.links.checkError')
                    : t(CHECK_FAILURE_KEY[checkResult.reason])}
                </p>
              )}
              {checkResult?.ok &&
                (checkResult.ordersChecked === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('stepCustomFiles.links.noOrders')}</p>
                ) : (
                  <>
                    {(
                      [
                        ['front', checkResult.front],
                        ['back', checkResult.back],
                      ] as const
                    )
                      .filter(([, side]) => side.configured)
                      .map(([name, side]) => {
                        const ok = side.found && side.https && side.allowed;
                        return (
                          <p key={name} className={ok ? 'text-xs text-emerald-700' : 'text-xs text-amber-900'}>
                            {t(sideResultKey(side), {
                              side:
                                name === 'front'
                                  ? t('stepCustomFiles.links.side.front')
                                  : t('stepCustomFiles.links.side.back'),
                            })}
                          </p>
                        );
                      })}
                    <p className="text-xs text-muted-foreground">
                      {t('stepCustomFiles.links.ordersChecked', { count: checkResult.ordersChecked })}
                    </p>
                  </>
                ))}
            </div>
          </div>
        )}

        <div aria-live="polite" className="min-h-0">
          {message && (
            <p className={message.tone === 'ok' ? 'text-xs text-emerald-700' : 'text-xs text-destructive'}>
              {message.text}
            </p>
          )}
        </div>
      </div>
    </StepPane>
  );
}
